/* i18n-ignore: interne adminpagina, alleen NL */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Pencil, Plus, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminNav } from "@/components/admin/AdminNav";
import { adminProjectsQuery } from "@/lib/projects-queries";
import { projectTypeLabel, projectTypes } from "@/content/project-types";

export const Route = createFileRoute("/_authenticated/admin/realisaties/")({
  staticData: { sitemap: false },
  head: () => ({ meta: [{ title: "Realisaties beheren — Gopro Projects" }, { name: "robots", content: "noindex" }] }),
  component: AdminProjectsPage,
});

function AdminProjectsPage() {
  const { data, isLoading, isError } = useQuery(adminProjectsQuery);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("alle");
  const [gemeente, setGemeente] = useState("alle");

  const gemeenten = useMemo(
    () => [...new Set((data ?? []).map((project) => project.gemeente).filter(Boolean))].sort(),
    [data],
  );

  const visible = (data ?? []).filter((project) => {
    const term = search.trim().toLowerCase();
    return (
      (term === "" || project.titel.toLowerCase().includes(term) || project.slug.includes(term)) &&
      (type === "alle" || project.typeWerk === type) &&
      (gemeente === "alle" || project.gemeente === gemeente)
    );
  });

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <AdminNav />
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Realisaties</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Maak een realisatie met foto's, gegevens en een klantquote, en zet ze online.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/realisaties/$slug" params={{ slug: "nieuw" }}>
            <Plus className="mr-2 h-4 w-4" /> Nieuwe realisatie
          </Link>
        </Button>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="zoek" className="text-xs">
            Zoeken
          </Label>
          <Input id="zoek" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Titel of webadres" />
        </div>
        <div>
          <Label htmlFor="type" className="text-xs">
            Soort werk
          </Label>
          <select
            id="type"
            value={type}
            onChange={(event) => setType(event.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="alle">Alle soorten</option>
            {projectTypes.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="gemeente" className="text-xs">
            Gemeente
          </Label>
          <select
            id="gemeente"
            value={gemeente}
            onChange={(event) => setGemeente(event.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="alle">Alle gemeenten</option>
            {gemeenten.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      )}

      {isError && (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            De realisaties konden niet geladen worden. Herlaad de pagina of probeer later opnieuw.
          </CardContent>
        </Card>
      )}

      {data && visible.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Geen realisaties gevonden met deze keuze.
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {visible.map((project) => (
          <Card key={project.id}>
            <CardContent className="flex flex-wrap items-center gap-4 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={project.status === "gepubliceerd" ? "default" : "secondary"}>
                    {project.status === "gepubliceerd" ? "Online" : "Concept"}
                  </Badge>
                  {project.uitgelicht && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Star className="h-3 w-3" /> Uitgelicht
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">{projectTypeLabel(project.typeWerk)}</span>
                  {project.gemeente && <span className="text-xs text-muted-foreground">{project.gemeente}</span>}
                  <span className="text-xs text-muted-foreground">Volgorde {project.sortOrder}</span>
                </div>
                <p className="mt-1 truncate font-semibold">{project.titel}</p>
                <p className="truncate text-sm text-muted-foreground">/realisaties/{project.slug}</p>
              </div>
              <Button asChild size="sm">
                <Link to="/admin/realisaties/$slug" params={{ slug: project.slug }}>
                  <Pencil className="mr-1 h-4 w-4" /> Bewerken
                </Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </main>
  );
}
